/**
 * Loading shapes for the Network Collectors surfaces — the list, one
 * Collector's detail page, one Log Source's manage page, and both wizards.
 * **One plural, multi-export skeleton file per feature** (A-03); the sections they stand in for are `lazy()`, so these have to be in the
 * eager bundle to be what renders while a chunk arrives.
 *
 * Everything that does not need a server response renders for real (P-05): the
 * status filter (it reads the URL, and what you picked is already applied when
 * the rows land), the row's `Manage` button (real, disabled — there is no id to
 * link to yet), the back link, the sources table's chrome, and every label. The
 * KPI strip is the one documented exception — see `KpiStripSkeleton`.
 *
 * The two differ on the page header, and correctly: the **list**'s renders above
 * the boundary and never unmounts, so drawing a placeholder for it would be
 * drawing over something already on screen; the **detail**'s header *is* data
 * (name, address, liveness), so it has to be part of the loading shape.
 */
import { getRouteApi } from '@tanstack/react-router'

import { cn } from '@/utils/ui/styles'

import BackLink from '@/components/common/back-link'
import { KpiStripSkeleton } from '@/components/skeletons/kpi-strip-skeleton'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'

import { RUN_EXPLANATION } from '../diagnostics-summary'
import { resolveCollectorStatusFilter } from '../status-filter'
import {
	AddCollectorFrame,
	HostRequirements,
	InstallCommandFrame,
	StaticIpAcknowledgement,
} from './add-collector/wizard-frame'
import {
	AddLogSourceFrame,
	AdvancedSection,
	REGISTER_STEP,
	RegisterSourceCard,
	TransportToggle,
} from './add-log-source/wizard-frame'
import { LogSourceTableFrame } from './log-source-table-frame'
import { LogSourcePanelFrame } from './panel-frame'
import { CollectorStatusFilterToggle } from './status-filter-toggle'

const Route = getRouteApi('/_dashboard/integrations/collectors/')

/**
 * The list, with only the data taken out.
 *
 * Used as **both** the route's `pendingComponent` and the section's `Suspense`
 * fallback, so the two states cannot drift apart (P-05 §2).
 */
export const CollectorsListSkeleton = () => {
	const statusParam = Route.useSearch({ select: (search) => search.status })

	return (
		<div className="flex flex-col gap-4">
			<KpiStripSkeleton count={3} />

			<CollectorStatusFilterToggle
				value={resolveCollectorStatusFilter(statusParam)}
				counts={null}
				onChange={() => {}}
			/>

			{/* Four rows: what the fixture returns, and a plausible estate size. A
			    count that undershoots is the worse error — the page grows downwards
			    as rows land, which is the shift the skeleton exists to prevent. */}
			<div className="mb-8 flex flex-col gap-4">
				{Array.from({ length: 4 }).map((_, index) => (
					<CollectorRowSkeleton key={index} />
				))}
			</div>
		</div>
	)
}

/**
 * One collapsed row. Mirrors `CollectorRow`'s element structure rather than
 * asserting a height, so the row's own padding and line boxes set the size and the
 * two cannot disagree.
 */
const CollectorRowSkeleton = () => {
	return (
		<Card className="gap-0 p-0">
			<div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
				<div className="flex min-w-0 flex-1 items-center gap-3">
					<Skeleton className="size-1.5 rounded-full" />
					<Skeleton className="h-5 w-48" />
				</div>

				<div className="flex items-center gap-4">
					{/* `Pill` is a fixed h-5.5 by design (ADR 0020) — one height, no variants. */}
					<Skeleton className="h-5.5 w-24 rounded-full" />

					{/* Matches the row's own block: an `h-5` line (the `text-sm`
					    reading) above the real `text-xs` label, no gap. */}
					<div className="flex flex-col items-end">
						<Skeleton className="h-5 w-16" />
						<p className="text-muted-foreground text-xs tracking-wide uppercase">
							Last seen
						</p>
					</div>

					<Skeleton className="h-8 w-24" />

					<Button variant="outline" size="sm" disabled>
						Manage
					</Button>
				</div>
			</div>
		</Card>
	)
}

/**
 * One Collector's detail page, with only the data taken out.
 *
 * Used as **both** the route's `pendingComponent` and the section's `Suspense`
 * fallback, so the two states cannot drift apart (P-05 §2).
 */
export const CollectorDetailSkeleton = () => {
	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-3">
				<div className="flex items-center gap-3">
					<BackLink to="/integrations/collectors" />
					{/* `SectionTitle` is `text-3xl font-bold` — a 36px line box. */}
					<div className="flex h-9 items-center">
						<Skeleton className="h-7 w-56" />
					</div>
					{/* `Pill` is a fixed h-5.5 by design (ADR 0020). */}
					<Skeleton className="h-5.5 w-24 rounded-full" />
				</div>

				{/* The meta line's own `text-sm` line box (20px), so the strip below
				    does not step up when the address lands. */}
				<div className="flex h-5 items-center">
					<Skeleton className="h-4 w-96" />
				</div>
			</div>

			<KpiStripSkeleton count={3} />

			{/* The frame is the real one — column headers and the status explanation
			    are fixed per position (P-05 §1), and sharing it with the resolved
			    table is what stops the two drifting a column apart. */}
			<LogSourceTableFrame>
				{/* Four rows: the largest Collector in the fixtures, and a plausible
				    one. Undershooting is the worse error — the page grows downwards as
				    rows land, which is the shift the skeleton exists to prevent. */}
				{Array.from({ length: 4 }).map((_, rowIndex) => (
					<TableRow key={rowIndex}>
						{CELL_WIDTHS.map((width, cellIndex) => (
							<TableCell key={width} className={cn(cellIndex === 0 && 'px-4')}>
								<Skeleton className={cn('h-4', width)} />
							</TableCell>
						))}
						<TableCell className="pr-4 text-right">
							<Skeleton className="ml-auto h-8 w-20" />
						</TableCell>
					</TableRow>
				))}
			</LogSourceTableFrame>
		</div>
	)
}

/**
 * Placeholder width per sources-table column, so the grey bars read as a table
 * rather than a block. In `LogSourceTableFrame`'s column order.
 */
const CELL_WIDTHS = ['w-56', 'w-16', 'w-20', 'w-24', 'w-14']

/**
 * One Log Source's manage page, with only the data taken out.
 *
 * Used as **both** the route's `pendingComponent` and the section's `Suspense`
 * fallback, so the two states cannot drift apart (P-05 §2).
 *
 * The panels are the real frames with their real buttons (disabled — there is
 * nothing to run against yet), because everything about them except the source's
 * endpoint is fixed per position (P-05 §1). What is greyed is exactly what the
 * query supplies: the source's name, its status pill, the meta line, and the two
 * sentences that name its port.
 */
export const LogSourceDetailSkeleton = () => {
	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-3">
				<div className="flex items-center gap-3">
					{/* No `params`: the collector id is in the URL, but the skeleton is
						rendered by the route before anything resolves, and history is what
						this link uses in practice. */}
					<BackLink to="/integrations/collectors" />
					{/* `SectionTitle` is `text-3xl font-bold` — a 36px line box. */}
					<div className="flex h-9 items-center">
						<Skeleton className="h-7 w-72" />
					</div>
					{/* `Pill` is a fixed h-5.5 by design (ADR 0020). */}
					<Skeleton className="h-5.5 w-24 rounded-full" />
				</div>

				{/* The meta line's own `text-sm` line box (20px), so the panels below do
					not step up when it lands. */}
				<div className="flex h-5 items-center">
					<Skeleton className="h-4 w-[36rem]" />
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
				<LogSourcePanelFrame
					title="Recent logs"
					action={
						<Button variant="outline" size="sm" disabled>
							Capture
						</Button>
					}
				>
					<PanelBodySkeleton />
				</LogSourcePanelFrame>

				<LogSourcePanelFrame
					title="Diagnostics"
					action={
						<Button size="sm" disabled>
							Run diagnostics
						</Button>
					}
				>
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
						<Label className="text-muted-foreground shrink-0">
							Device IP (optional)
						</Label>
						<Input placeholder="10.44.2.7" disabled className="font-mono" />
					</div>

					<PanelBodySkeleton />

					<p className="text-muted-foreground text-xs">{RUN_EXPLANATION}</p>
				</LogSourcePanelFrame>
			</div>
		</div>
	)
}

/**
 * The idle body both panels open with: a glyph, a headline and two lines of
 * explanation. Mirrors that block's element structure rather than asserting a
 * height, so the resolved panel's own padding sets the size.
 */
const PanelBodySkeleton = () => {
	return (
		<div className="flex flex-col items-center gap-2 py-8">
			<Skeleton className="size-5 rounded-full" />
			<div className="flex h-6 items-center">
				<Skeleton className="h-5 w-40" />
			</div>
			<Skeleton className="h-4 w-80" />
			<Skeleton className="h-4 w-64" />
		</div>
	)
}

/**
 * The Add Collector wizard, while its step's chunk arrives.
 *
 * The one skeleton here that stands in for **no server call at all** — the step
 * fetches nothing, so this is purely what the code-split boundary shows (P-21).
 * Everything that does not depend on what the operator types renders for real:
 * the wizard chrome, the requirements, the section labels, the acknowledgement
 * and both actions (real, disabled — there is nothing to acknowledge or continue
 * to yet). Only the name field, the command and the connection band are drawn as
 * shapes, because all three are derived from a name that does not exist until
 * the step mounts.
 */
export const AddCollectorSkeleton = () => {
	return (
		<AddCollectorFrame
			footer={
				<>
					<Button variant="outline" disabled>
						Back
					</Button>
					<Button disabled>Next</Button>
				</>
			}
		>
			<div className="flex flex-col gap-2">
				<Label>Collector name</Label>
				<Input disabled />
				<div className="flex h-4 items-center">
					<Skeleton className="h-3 w-96" />
				</div>
			</div>

			<HostRequirements />

			<InstallCommandFrame
				// The copy button's own 28px box, so the header row does not change
				// height when the real one lands.
				action={<Skeleton className="size-7" />}
			>
				{/* The command's own four line boxes — two lines, the blank one, and
					the trailing comment — at the `pre`'s leading, so the card below does
					not step down by a line and a half when the real one lands. */}
				<div className="flex flex-col p-3 font-mono text-xs leading-relaxed">
					{['w-[28rem]', 'w-64', null, 'w-[22rem]'].map((width, index) => (
						<div key={index} className="flex h-[1.625em] items-center">
							{width ? <Skeleton className={cn('h-3', width)} /> : null}
						</div>
					))}
				</div>
			</InstallCommandFrame>

			<StaticIpAcknowledgement disabled />

			<div className="bg-muted/30 flex items-start gap-3 rounded-lg border p-3">
				{/* Both lines sit in the band's own `text-sm` line boxes (20px), for the
					reason the command block's do. */}
				<div className="flex h-5 items-center">
					<Skeleton className="size-4 rounded-full" />
				</div>
				<div className="flex flex-col gap-0.5">
					<div className="flex h-5 items-center">
						<Skeleton className="h-4 w-64" />
					</div>
					<div className="flex h-5 items-center">
						<Skeleton className="h-4 w-96" />
					</div>
				</div>
			</div>
		</AddCollectorFrame>
	)
}

/**
 * The Add Log Source wizard, while its step's chunk arrives and the catalog and
 * Collectors answer.
 *
 * Everything that does not depend on either answer renders for real (P-05 §1):
 * the wizard chrome, the rail on step one, the card's explanation, every field
 * label, the transport control, the Advanced summary and both actions — real,
 * disabled, because there is nothing yet to go back from or continue to.
 *
 * Placeholders stand only where a server response decides the content: the
 * subtitle (it names the Log Source Type), the three field values, and the port
 * hint (it names the type too). The Advanced panel draws nothing because it is
 * collapsed — a placeholder inside a closed accordion is one nobody can see.
 */
export const AddLogSourceSkeleton = () => {
	return (
		<AddLogSourceFrame
			activeStep={REGISTER_STEP}
			subtitle={
				// `Skeleton` renders a `div`, which is invalid inside
				// `SectionDescription`'s `p` — the placeholder is a plain span styled
				// to match it, sitting in the subtitle's own `text-sm` line box so the
				// card below does not step down when the real sentence lands.
				<span className="bg-muted inline-block h-3.5 w-80 animate-pulse rounded-md align-middle" />
			}
			footer={
				<>
					<Button variant="outline" disabled>
						Back
					</Button>
					<Button disabled>Continue</Button>
				</>
			}
		>
			<RegisterSourceCard>
				<div className="flex flex-col gap-2">
					<Label>Source name</Label>
					<Input disabled />
					<div className="flex h-4 items-center">
						<Skeleton className="h-3 w-[30rem]" />
					</div>
				</div>

				<div className="flex flex-col gap-2">
					<Label>Receive on collector</Label>
					{/* `SelectTrigger`'s own 32px box (`data-[size=default]:h-8`), so the
						row does not resize when the Collectors land. */}
					<Skeleton className="h-8 w-full rounded-lg" />
					<div className="flex h-4 items-center">
						<Skeleton className="h-3 w-[26rem]" />
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-2">
						<Label>Transport</Label>
						<TransportToggle disabled />
						<p className="text-muted-foreground text-xs">
							UDP/TCP only — internal traffic isn’t TLS-wrapped.
						</p>
					</div>

					<div className="flex flex-col gap-2">
						<Label>Port</Label>
						<Input disabled />
						<div className="flex flex-col">
							{['w-full', 'w-40'].map((width) => (
								<div key={width} className="flex h-4 items-center">
									<Skeleton className={cn('h-3', width)} />
								</div>
							))}
						</div>
					</div>
				</div>

				<AdvancedSection disabled />
			</RegisterSourceCard>
		</AddLogSourceFrame>
	)
}
